import { useRef, useState } from 'react';
import { Mic, Paperclip, Send, Square } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';

import { AtendimentoQuickActions } from './AtendimentoQuickActions';

type Props = {
  conversationId: string;
  portal?: boolean;
  onSent: () => Promise<void> | void;
  /** Exibe chips de ações rápidas (portal). */
  showQuickActions?: boolean;
  isCliente?: boolean;
};

export function AtendimentoComposer({
  conversationId,
  portal = true,
  onSent,
  showQuickActions = false,
  isCliente = true,
}: Props) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const mediaRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  const base = portal
    ? `/rique-atendimento/portal/conversations/${conversationId}`
    : `/rique-atendimento/conversations/${conversationId}`;

  const sendText = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post(`${base}/operator-messages`, { content: text.trim() });
      setText('');
      await onSent();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Falha ao enviar');
    } finally {
      setSending(false);
    }
  };

  const sendFile = async (file: File) => {
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (text.trim()) fd.append('caption', text.trim());
      await api.post(`${base}/attachments`, fd);
      setText('');
      await onSent();
      toast.success('Anexo enviado');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Falha no anexo');
    } finally {
      setSending(false);
    }
  };

  const sendAudioBlob = async (blob: Blob) => {
    setSending(true);
    try {
      const fd = new FormData();
      const ext = blob.type.includes('ogg') ? 'ogg' : blob.type.includes('mp4') ? 'm4a' : 'webm';
      fd.append('audio', blob, `voz.${ext}`);
      await api.post(`${base}/audio`, fd);
      await onSent();
      toast.success('Áudio enviado');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Falha no áudio');
    } finally {
      setSending(false);
    }
  };

  const toggleRecord = async () => {
    if (recording && mediaRef.current) {
      mediaRef.current.stop();
      setRecording(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : MediaRecorder.isTypeSupported('audio/mp4')
          ? 'audio/mp4'
          : '';
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (ev) => {
        if (ev.data.size) chunksRef.current.push(ev.data);
      };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        void sendAudioBlob(blob);
      };
      mediaRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      toast.error('Microfone indisponível');
    }
  };

  return (
    <div className="border-t space-y-2">
      {portal && showQuickActions ? (
        <AtendimentoQuickActions
          conversationId={conversationId}
          onSent={onSent}
          isCliente={isCliente}
        />
      ) : null}
      <div className="p-2 flex gap-2">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={2}
          placeholder="Responder ao solicitante…"
          className="flex-1"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void sendText();
            }
          }}
        />
        <div className="flex flex-col gap-1">
          <Button
            size="sm"
            disabled={sending || !text.trim()}
            onClick={() => void sendText()}
            title="Enviar"
          >
            <Send className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={sending}
            onClick={() => fileRef.current?.click()}
            title="Anexo"
          >
            <Paperclip className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            variant={recording ? 'destructive' : 'outline'}
            disabled={sending}
            onClick={() => void toggleRecord()}
            title={recording ? 'Parar e enviar' : 'Gravar áudio'}
          >
            {recording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </Button>
        </div>
      </div>
      <input
        ref={fileRef}
        type="file"
        className="hidden"
        accept=".pdf,.png,.jpg,.jpeg,.webp,.docx,application/pdf,image/*,.docx"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) void sendFile(f);
        }}
      />
    </div>
  );
}
